import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import BreathingFlameMark from '../components/BreathingFlameMark';
import AuthSheet from '../components/AuthSheet';

/**
 * Home — the minimal app entry screen. Renders a single-viewport landing
 * with the brand mark, value prop, and Sign in / Get started CTAs. No
 * scroll required on mobile.
 *
 * Marketing content (mission, feature grid, founder statement, IronLimb
 * partnership context) lives at /story. Authed users hitting / are
 * redirected to /progress by App.js before Home renders.
 *
 * Props:
 *   authView — 'signin' | 'register' | null. Lets /login and /register
 *   routes render Home with the AuthSheet pre-opened to the right mode,
 *   preserving deep-linking and AuthGated redirect-with-state behavior.
 */
const Home = ({ authView = null }) => {
  const [sheet, setSheet] = useState(authView);
  const flameSrc = '/brand/enferno-flame-mark-v46.png';

  // Sync sheet open state when the route changes (e.g. user navigates from
  // /login → /register via the in-sheet toggle, App.js re-renders Home with
  // a different authView prop).
  useEffect(() => {
    setSheet(authView);
  }, [authView]);

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen flex flex-col">
      <section className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
        <div className="max-w-md w-full text-center">
          <div className="flex items-center justify-center mb-8" data-testid="home-flame-mark">
            <BreathingFlameMark size={180} intensity="lively" src={flameSrc} />
          </div>

          <h1 className="font-display text-5xl md:text-6xl tracking-wide leading-[0.95] mb-3" data-testid="home-tagline">
            Move with purpose.
          </h1>
          <p className="text-enf-text-secondary text-base leading-relaxed mb-10 max-w-sm mx-auto">
            Five pillars. Every discipline. EveryBody.
          </p>

          <div className="flex flex-col gap-3 max-w-xs mx-auto">
            <button
              type="button"
              onClick={() => setSheet('register')}
              data-testid="home-cta-get-started"
              className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast rounded-md font-semibold transition-colors uppercase tracking-wider text-sm"
            >
              Get started
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              type="button"
              onClick={() => setSheet('signin')}
              data-testid="home-cta-sign-in"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-transparent hover:bg-enf-surface-mid border border-enf-surface-border text-enf-text-primary rounded-md font-medium transition-colors"
            >
              Sign in
            </button>
          </div>

          <div className="mt-10 text-xs text-enf-text-muted">
            <Link to="/story" className="hover:text-enf-text-primary transition-colors" data-testid="home-story-link">
              About Enferno
            </Link>
            <span className="mx-2">·</span>
            <span>In partnership with <span className="text-enf-text-secondary">IronLimb Foundation</span></span>
          </div>
        </div>
      </section>

      <AuthSheet mode={sheet} onClose={() => setSheet(null)} />
    </div>
  );
};

export default Home;
